import {
  SERIES_PALETTE_SIZE,
  SeriesColor,
} from "@/shared/domain/dashboards/DashboardDefinition";

// Imported here rather than at the app entry so anything that can name a
// palette slot can also resolve one.
import "./seriesColors.css";

// Canvas can't resolve CSS custom properties, so read the concrete color off
// the element at draw time, with a fallback matching --primary-main.
export const FALLBACK_COLOR = "rgb(2, 136, 209)";

/**
 * The computed colour a CSS custom property yields on `el`.
 *
 * NOT `getComputedStyle(el).getPropertyValue("--x")`: that returns a custom
 * property SUBSTITUTED BUT UNCOMPUTED, so a token like
 * `light-dark(oklch(...), oklch(...))` — which is how the series palette is
 * declared — comes back verbatim. Assigning that to a canvas `fillStyle` is a
 * SILENT no-op, which drew every grouped series in Chart.js's default black
 * while the HTML legend, where the browser does compute `light-dark()`, showed
 * the right colours.
 *
 * Assigning the variable to a real property and reading it back gets the
 * computed value, which is always a resolved colour the canvas can parse. The
 * inline style is restored immediately; `color` has no effect on a canvas's
 * own painting, so nothing observable changes in between.
 */
export function computedColorOf(el: HTMLElement, variable: string): string {
  return computedColor(el, `var(${variable})`) || FALLBACK_COLOR;
}

/** The colour a CSS `<color>` expression computes to on `el`. The mechanism
 *  {@link computedColorOf} documents, shared with the hover derivation below:
 *  assign, read back the COMPUTED value, restore. */
function computedColor(el: HTMLElement, expression: string): string {
  const previous = el.style.color;
  el.style.color = expression;
  const resolved = getComputedStyle(el).color;
  el.style.color = previous;
  return resolved;
}

/** The CSS variable the palette slot at `index` names. Slots past the last one
 *  cycle back to the first: the palette bounds the COLOURS, never the number
 *  of series drawn. */
export function seriesColorVar(index: SeriesColor): string {
  return `--metric-series-${String(index % SERIES_PALETTE_SIZE)}`;
}

/**
 * Accessible names for each palette slot, tracking `seriesColors.css` row for
 * row. Second shades are marked "2" rather than "dark": that step is darker in
 * light mode and lighter in dark, so any lightness word would be wrong in one
 * theme.
 */
export const SERIES_COLOR_LABELS = [
  "Blue",
  "Green",
  "Red",
  "Magenta",
  "Gray",
  "Blue 2",
  "Green 2",
  "Red 2",
  "Magenta 2",
  "Gray 2",
] as const;

/**
 * The slots the picker offers: the first shade of each hue. Second shades sit
 * too close to their first to earn a swatch, but stay in the palette for
 * grouped charts. A card may still HOLD any slot; this only narrows the offer.
 */
export const PICKABLE_SERIES_COLORS: readonly SeriesColor[] = [0, 1, 2, 3, 4];

/**
 * The CSS variable each of `count` series takes its colour from — the single
 * source both the canvas and the HTML legend read, so a swatch and the line it
 * labels can never disagree.
 *
 * `chosen` (the card's picked slot) sets where the walk STARTS rather than
 * recolouring only the first series, which would let a later series of a
 * grouped chart land on the same slot.
 */
export function seriesColorVars(count: number, chosen?: SeriesColor): string[] {
  // Not slot 0: an uncoloured lone series keeps the theme accent, so cards
  // saved without a colour render unchanged.
  if (count <= 1 && chosen === undefined) {
    return ["--primary-main"];
  }
  const start = chosen ?? 0;
  return Array.from({ length: count }, (_unused, i) =>
    seriesColorVar(start + i),
  );
}

/**
 * The colour for each partition, resolved against `canvas` because a canvas
 * cannot resolve CSS variables itself. The slots come from
 * {@link seriesColorVars}.
 */
export function resolveSeriesColors(
  canvas: HTMLCanvasElement,
  count: number,
  chosen?: SeriesColor,
): string[] {
  return seriesColorVars(count, chosen).map((name) =>
    computedColorOf(canvas, name),
  );
}

/**
 * How far a hovered element's colour moves toward the page's foreground, and
 * what it moves toward.
 *
 * Toward the FOREGROUND rather than simply darker: `--gray-12` is near-black
 * in light mode and near-white in dark, so a hovered bar gains contrast
 * against the ground in both themes instead of receding into it in one. 20% in
 * oklab is about the size of the step Chart.js's own default hover makes
 * (10% darker, half a step more saturated) — a clear change that still reads
 * as the same series.
 */
const HOVER_MIX_TOWARD = "--gray-12";
const HOVER_MIX_PROPORTION = "20%";

/**
 * The hovered counterpart of each resting colour in `colors`.
 *
 * Chart.js has a default for this — `hoverBackgroundColor` derives from
 * `backgroundColor` by saturating and darkening it — but it derives it with a
 * parser that predates CSS Color 4 and so cannot read the `oklch(...)` the
 * foundation's scales compute to. It returns undefined, Chart.js drops the
 * option, and the hovered bar is painted with the canvas's default fill: BLACK,
 * on a chart whose every other bar is still its series colour. Only a
 * single-series chart escaped it, because `--primary-main` happens to be an
 * sRGB literal that parser can still read.
 *
 * So the mix is done by the browser, which understands every colour space the
 * palette can be written in, and the result is read back computed — the same
 * round-trip {@link computedColorOf} makes, for the same reason. Each colour is
 * derived from ITSELF, so a repainted palette carries its hover states with it.
 */
export function resolveHoverColors(
  canvas: HTMLCanvasElement,
  colors: string[],
): string[] {
  const toward = computedColorOf(canvas, HOVER_MIX_TOWARD);
  return colors.map((color) => {
    const mixed = `color-mix(in oklab, ${color}, ${toward} ${HOVER_MIX_PROPORTION})`;
    // An expression the browser cannot parse would be dropped as an invalid
    // declaration and read back as the element's INHERITED colour — a wrong
    // colour, painted confidently, which is the failure being fixed here. A
    // series that cannot be mixed keeps its own colour instead: it loses the
    // highlight, not its identity.
    if (!CSS.supports("color", mixed)) {
      return color;
    }
    return computedColor(canvas, mixed) || color;
  });
}
